import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import { adminOrderService } from '../../services'
import {
  CLEARED_BRANCH_FIELDS,
  filterPaymentBanks,
} from '../../utils/paymentProofFormState'

const BRANCH_SEARCH_DEBOUNCE_MS = 250
const BRANCH_SEARCH_LIMIT = 50

/**
 * Bank (searchable, client-side over ~60 banks) +
 * branch (async server search by name or routing number) for one payment proof.
 * Selected bank/branch labels come from the proof snapshot, so no full list is needed.
 */
export default function PaymentProofBankBranchFields({
  banks = [],
  proof,
  onPatch,
  errors = {},
  disabled = false,
}) {
  const bankId = proof?.bankId ?? null
  const [query, setQuery] = useState('')
  const [inputValue, setInputValue] = useState(proof?.branchName || '')
  const [branchOptions, setBranchOptions] = useState([])
  const [branchTotal, setBranchTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const cacheRef = useRef(new Map())

  // Reset the branch search whenever the bank changes.
  useEffect(() => {
    setQuery('')
  }, [bankId])

  useEffect(() => {
    setInputValue(proof?.branchName || '')
  }, [proof?.branchId, proof?.branchName])

  // Debounced branch search (cached per bank + query).
  useEffect(() => {
    if (!bankId) {
      setBranchOptions([])
      setBranchTotal(0)
      return undefined
    }
    const key = `${bankId}|${query.trim().toLowerCase()}`
    const cached = cacheRef.current.get(key)
    if (cached) {
      setBranchOptions(cached.items)
      setBranchTotal(cached.total)
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const res = await adminOrderService.getPaymentProofBranches(bankId, {
          q: query.trim(),
          limit: BRANCH_SEARCH_LIMIT,
        })
        const items = Array.isArray(res?.data) ? res.data : []
        const total = Number(res?.total ?? items.length)
        cacheRef.current.set(key, { items, total })
        if (!cancelled) {
          setBranchOptions(items)
          setBranchTotal(total)
        }
      } catch {
        if (!cancelled) {
          setBranchOptions([])
          setBranchTotal(0)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, BRANCH_SEARCH_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [bankId, query])

  const selectedBank =
    banks.find((b) => String(b.id) === String(bankId)) ||
    (bankId ? { id: bankId, name: proof?.bankName || `Bank #${bankId}` } : null)

  // Memoised so MUI sees a stable value; a new object each render makes
  // Autocomplete treat it as a value change and reset the typed input.
  const branchId = proof?.branchId ?? null
  const branchName = proof?.branchName
  const branchRouting = proof?.routingNumber
  const selectedBranch = useMemo(
    () =>
      branchId
        ? {
            id: branchId,
            name: branchName || `Branch #${branchId}`,
            routing_number: branchRouting || '',
          }
        : null,
    [branchId, branchName, branchRouting],
  )

  // Keep the current selection in the option list (avoids MUI "invalid value" warnings).
  const branchSelectOptions =
    selectedBranch &&
    !branchOptions.some((br) => String(br.id) === String(selectedBranch.id))
      ? [selectedBranch, ...branchOptions]
      : branchOptions
  const bankSelectOptions =
    selectedBank && !banks.some((b) => String(b.id) === String(selectedBank.id))
      ? [selectedBank, ...banks]
      : banks

  let branchHelper = 'Search by branch name'
  if (errors.branchId) branchHelper = errors.branchId
  else if (branchTotal > branchOptions.length) {
    branchHelper = `Showing ${branchOptions.length} of ${branchTotal} — type to narrow down`
  }

  return (
    <>
      <Autocomplete
        size="small"
        fullWidth
        disabled={disabled}
        options={bankSelectOptions}
        value={selectedBank}
        getOptionLabel={(b) => b?.name || ''}
        isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
        filterOptions={(opts, state) => filterPaymentBanks(opts, state.inputValue)}
        onChange={(_, bank) =>
          onPatch({
            bankId: bank ? Number(bank.id) : null,
            bankName: bank?.name || '',
            ...CLEARED_BRANCH_FIELDS,
          })
        }
        renderOption={(props, bank) => (
          <li {...props} key={bank.id}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
              <span>{bank.name}</span>
              {bank.code ? (
                <Typography variant="caption" color="text.secondary">
                  {bank.code}
                </Typography>
              ) : null}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Bank"
            placeholder="Search bank by name or code"
            error={Boolean(errors.bankId)}
            helperText={errors.bankId || undefined}
          />
        )}
      />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1, minWidth: 260 }}
          disabled={disabled || !bankId}
          options={branchSelectOptions}
          value={selectedBranch}
          inputValue={inputValue}
          loading={loading}
          filterOptions={(x) => x}
          getOptionLabel={(br) => br?.name || ''}
          isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
          noOptionsText={query ? 'No branches found' : 'No branches'}
          onInputChange={(_, value, reason) => {
            setInputValue(value)
            if (reason === 'input') setQuery(value)
            if (reason === 'clear') setQuery('')
          }}
          onChange={(_, br) =>
            onPatch({
              branchId: br ? Number(br.id) : null,
              branchName: br?.name || '',
              routingNumber: br?.routing_number || '',
            })
          }
          renderOption={(props, br) => (
            <li {...props} key={br.id}>
              <Typography variant="body2">{br.name}</Typography>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Branch"
              placeholder={bankId ? 'Search branch' : 'Select a bank first'}
              error={Boolean(errors.branchId)}
              helperText={branchHelper}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </>
  )
}
